import React from 'react';
import { parseDistribution, probAfter } from './PredictionDistributionBar';
import './ArrivalOutlook.css';

// Touchdown -> on-block (gate) offset. The model predicts touchdown; the
// scheduled time (STA) is gate arrival, so an "on-time at gate" flight touches
// down ~TAXI_MIN earlier. Without this the outlook reads everything as early.
// Measured against production: taxi-in vs our touchdown is 5.0-6.1 min (n=437).
export const TAXI_MIN = 6;

/**
 * How far from the scheduled GATE arrival still counts as on-time, in minutes.
 *
 * <p>ONE definition, deliberately. This used to be ±10 here while the delay chip beside it used
 * +15, so a single row published two probabilities for one question — "91% delay" next to "95%
 * late" — that differed only because the two thresholds were 5 minutes apart. Read together they
 * looked like two measurements agreeing; they were one measurement quoted twice.
 *
 * <p>15 is the industry A15 convention (a flight is "on time" within 15 min of schedule), which is
 * also what the chip was already using and what people expect when they read "late".
 */
export const ON_TIME_TOLERANCE_MIN = 15;

function toMs(s) {
  if (!s) return null;
  if (s instanceof Date) return s.getTime();
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : s + 'Z');
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}
const clamp01 = x => Math.max(0, Math.min(1, x));

/**
 * Probability the arrival lands >10 min EARLY / within ±10 min / >10 min LATE,
 * measured against the scheduled arrival (STA − taxi), falling back to the
 * airline ETA, then to the model's own median (a pure-spread indicator).
 *
 * Input: a flight row OR a prediction-event-like object with distributionJson,
 * predictionAt (base time of the distribution), scheduledArrival, airlineEstimatedArrival.
 * Returns { early, within, late, refType } in [0,1] probabilities, or null.
 */
export function arrivalOutlook(o) {
  if (!o) return null;
  const dist = parseDistribution(o.distributionJson);
  if (!dist) return null;
  const baseMs = toMs(o.predictionAt || o.distributionPredictionAt || o.lastPredictionAt);
  if (baseMs == null) return null;

  let refMs = toMs(o.scheduledArrival);
  let refType = 'scheduled';
  if (refMs == null) { refMs = toMs(o.airlineEstimatedArrival); refType = 'airline'; }

  const tol = ON_TIME_TOLERANCE_MIN;

  if (refMs == null) {
    // No reference time — fall back to spread vs the model's own median.
    const med = dist.p50Minutes;
    if (med == null) return null;
    const early = clamp01(1 - probAfter(dist, med - tol));
    const late = clamp01(probAfter(dist, med + tol));
    return { early, within: Math.max(0, 1 - early - late), late, refType: 'spread', tol };
  }

  // Predicted-touchdown minute that corresponds to an on-time gate arrival.
  const refMin = (refMs - baseMs) / 60000 - TAXI_MIN;
  const early = clamp01(1 - probAfter(dist, refMin - tol));
  const late = clamp01(probAfter(dist, refMin + tol));
  // gateMs: what the median touchdown means at the gate — the number the SCHED column can
  // actually be compared against. Without it the table shows a touchdown time next to a gate
  // time and invites a subtraction that is ~TAXI_MIN short of what the probability is computed on.
  const medGateMs = dist.p50Minutes != null
    ? baseMs + (dist.p50Minutes + TAXI_MIN) * 60000
    : null;
  return { early, within: Math.max(0, 1 - early - late), late, refType, refMin, tol, medGateMs, refMs };
}

const pct = x => `${Math.round(x * 100)}%`;

/**
 * Rotating rate arrow: angle encodes the rate of change of a probability.
 * North (↑) = rising fast, East (→) = steady, South (↓) = falling fast, with
 * NE/SE for moderate rates. `rate` is the change in probability (0..1) over the
 * trend window.
 */
function RateArrow({ rate }) {
  if (rate == null) return null;
  const RMAX = 0.12;                                  // 12 pts over the window = full vertical
  const frac = Math.max(-1, Math.min(1, rate / RMAX));
  const deg = -frac * 90;                             // +rate → up (north), −rate → down (south)
  const pts = Math.round(rate * 100);
  const title = Math.abs(pts) < 1 ? 'steady'
    : (pts > 0 ? `rising +${pts} pts` : `falling ${pts} pts`);
  return (
    <span className="ao-rate" title={title}
      style={{ display: 'inline-block', transform: `rotate(${deg}deg)`, transition: 'transform 0.4s ease' }}>→</span>
  );
}

function TrendArrow({ delta }) {
  if (delta == null) return null;
  // delta = change in late-probability vs an earlier cycle.
  let dir = 'flat', glyph = '→', title = 'delay risk steady';
  if (delta >= 0.04) { dir = 'up'; glyph = '↑'; title = `delay risk rising (+${Math.round(delta * 100)} pts)`; }
  else if (delta <= -0.04) { dir = 'down'; glyph = '↓'; title = `delay risk falling (${Math.round(delta * 100)} pts)`; }
  return <span className={`ao-trend ao-trend-${dir}`} title={title}>{glyph}</span>;
}

/**
 * 3-segment early / on-time / late bar. `compact` renders the row version
 * (bar + dominant outcome + trend arrow); otherwise the full labelled version.
 */
export function ArrivalOutlookBar({ outlook, trendDelta = null, trendRates = null, compact = false }) {
  if (!outlook) return null;
  const { early, within, late, refType } = outlook;
  const refLabel = refType === 'scheduled' ? 'vs schedule'
    : refType === 'airline' ? 'vs airline ETA' : 'spread (no schedule)';
  // With no scheduled or airline time there is no punctuality to report: the
  // three segments are measured against OUR OWN median, so "on-time" would mean
  // "close to our own guess". Say what it actually is.
  const isSpread = refType === 'spread';
  const tol = outlook.tol != null ? outlook.tol : ON_TIME_TOLERANCE_MIN;
  // Spell out the two things the numbers depend on that the table does not show: that we compare
  // at the GATE (the ETA column is touchdown, the schedule is on-block), and where "late" starts.
  // As a delta rather than a clock time — this component has no airport, so it cannot localise one,
  // and a UTC time printed beside the table's local ones would be an hour out for half the year.
  const basis = (() => {
    if (outlook.medGateMs == null || outlook.refMs == null) {
      return `On-time means arriving within ${tol} min of the scheduled time.`;
    }
    const d = Math.round((outlook.medGateMs - outlook.refMs) / 60000);
    const vs = d === 0 ? 'on schedule at the gate'
      : `${Math.abs(d)} min ${d > 0 ? 'later' : 'earlier'} than scheduled at the gate`;
    return `We expect to be ${vs} — the ETA column is touchdown, and the schedule measures`
      + ` on-block, so ~${TAXI_MIN} min of taxi is added before comparing.`
      + ` On-time means within ${tol} min.`;
  })();
  const title = isSpread
    ? `No scheduled time — this is our own estimate's spread, not punctuality: `
      + `${pct(early)} more than ${tol} min earlier than our estimate · `
      + `${pct(within)} within ±${tol} min of it · ${pct(late)} more than ${tol} min later`
    : `${pct(early)} early · ${pct(within)} on-time · ${pct(late)} late (${refLabel}). ${basis}`;
  const seg = (cls, p) => p > 0.001
    ? <div className={`ao-seg ${cls}`} style={{ flexGrow: p }} /> : null;

  if (compact) {
    // Dominant outcome drives the inline label.
    const dom = isSpread
      // Not a punctuality claim — the confidence that we land within 10 minutes
      // of our own estimate.
      ? { t: `${pct(within)} ±10m`, c: 'spread' }
      : late >= within && late >= early ? { t: `${pct(late)} late`, c: 'late' }
      : within >= early ? { t: `${pct(within)} on-time`, c: 'ontime' }
      : { t: `${pct(early)} early`, c: 'early' };
    return (
      <span className="ao-compact" title={title}>
        <span className={`ao-bar ${refType === 'spread' ? 'ao-spread' : ''}`}>
          {seg('ao-early', early)}{seg('ao-ontime', within)}{seg('ao-late', late)}
        </span>
        <span className={`ao-dom ao-dom-${dom.c}`}>{dom.t}</span>
        <TrendArrow delta={trendDelta} />
      </span>
    );
  }

  return (
    <div className="ao-full" title={title}>
      <div className={`ao-bar ao-bar-lg ${refType === 'spread' ? 'ao-spread' : ''}`}>
        {seg('ao-early', early)}{seg('ao-ontime', within)}{seg('ao-late', late)}
      </div>
      <div className="ao-legend">
        <span className="ao-lg ao-early">▮ {pct(early)} {isSpread ? 'earlier' : 'early'} <RateArrow rate={trendRates?.early} /></span>
        <span className="ao-lg ao-ontime">▮ {pct(within)} {isSpread ? 'within ±10m' : 'on-time'} <RateArrow rate={trendRates?.within} /></span>
        <span className="ao-lg ao-late">▮ {pct(late)} {isSpread ? 'later' : 'late'} <RateArrow rate={trendRates?.late ?? trendDelta} /></span>
        <span className="ao-ref">{refLabel}</span>
      </div>
    </div>
  );
}

/**
 * Tiny SVG sparkline of late-probability across prediction cycles (oldest→newest).
 * points: [{ t:number(ms), late:number(0..1) }]. Shows whether delay risk is
 * trending up or down over the flight's history.
 */
export function LateTrendSparkline({ points, width = 120, height = 26 }) {
  if (!points || points.length < 2) return null;
  const xs = points.map(p => p.t);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const spanX = Math.max(1, maxX - minX);
  const X = t => ((t - minX) / spanX) * (width - 2) + 1;
  const Y = v => height - 2 - clamp01(v) * (height - 4);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${X(p.t).toFixed(1)},${Y(p.late).toFixed(1)}`).join(' ');
  const last = points[points.length - 1].late;
  const first = points[0].late;
  const cls = last - first >= 0.04 ? 'up' : last - first <= -0.04 ? 'down' : 'flat';
  return (
    <svg className={`ao-spark ao-spark-${cls}`} width={width} height={height}
         viewBox={`0 0 ${width} ${height}`} aria-label="late-probability trend">
      <line x1="1" y1={Y(0)} x2={width - 1} y2={Y(0)} className="ao-spark-base" />
      <path d={d} className="ao-spark-line" fill="none" />
      <circle cx={X(maxX)} cy={Y(last)} r="2.4" className="ao-spark-dot" />
    </svg>
  );
}
